import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";
import { ProgressRing } from "@/components/ProgressRing";
import { Certificate } from "@/components/Certificate";
import { toast } from "sonner";
import { ArrowLeft, Zap, CheckCircle2, Circle, Loader2, Trophy, Award, Sparkles } from "lucide-react";

export default function ChallengeDetail() {
  const { cid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ch, setCh] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [xp, setXp] = useState(0);
  const [busy, setBusy] = useState(null);
  const [showCert, setShowCert] = useState(false);

  const load = () => api.get(`/challenges/${cid}`).then((r) => {
    setCh(r.data); setCompleted(r.data.completed_steps || []); setXp(r.data.xp_earned || 0);
  }).catch(() => navigate("/challenges"));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);

  const start = async () => { await api.post(`/challenges/${cid}/start`); load(); };

  const toggle = async (stepId) => {
    setBusy(stepId);
    try {
      const { data } = await api.post(`/challenges/${cid}/toggle`, { step_id: stepId });
      setCompleted(data.completed_steps); setXp(data.xp_earned);
      setCh((p) => ({ ...p, completed: data.completed, enrolled: true }));
      if (data.just_completed) { setShowCert(true); toast.success("Challenge complete! Badge unlocked 🏆"); }
    } catch (e) { toast.error("Could not update step"); }
    finally { setBusy(null); }
  };

  if (!ch) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-500" /></div>;

  const total = ch.steps.length;
  const percent = total ? Math.round((completed.length / total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="challenge-detail-page">
      <Link to="/challenges" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="challenge-back"><ArrowLeft size={16} /> All challenges</Link>

      <div className="eco-card p-6 md:p-7">
        <div className="flex items-start gap-5">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${ch.color}1a`, border: `1px solid ${ch.color}33` }}>
            <Icon name={ch.icon} size={28} style={{ color: ch.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight">{ch.title}</h1>
            <p className="text-slate-400 mt-1.5">{ch.description}</p>
            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
              <span>{ch.duration_days} days · {total} steps</span>
              <span className="flex items-center gap-1 text-amber-400"><Zap size={13} /> {xp}/{ch.xp_total} XP</span>
            </div>
          </div>
          <div className="hidden sm:block shrink-0"><ProgressRing value={percent} size={64} color={ch.color} /></div>
        </div>
        {ch.completed && (
          <button onClick={() => setShowCert(true)} data-testid="view-certificate-btn" className="btn-accent mt-5 w-full sm:w-auto"><Award size={16} /> View & download certificate</button>
        )}
      </div>

      {!ch.enrolled ? (
        <div className="eco-card p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center mx-auto mb-4"><Trophy size={26} className="text-amber-400" /></div>
          <h3 className="font-display text-xl font-semibold text-white">Ready to take on this challenge?</h3>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">Complete one step at a time, earn {ch.xp_total} XP, and unlock the <span className="text-amber-400 font-medium">{ch.badge.title}</span> badge.</p>
          <button onClick={start} data-testid="start-challenge-btn" className="btn-accent mt-6 mx-auto"><Sparkles size={16} /> Start challenge</button>
        </div>
      ) : (
        <div className="space-y-3">
          {ch.steps.map((s) => {
            const done = completed.includes(s.id);
            return (
              <div key={s.id} className={`eco-card p-5 transition-all ${done ? "opacity-80" : ""}`} data-testid={`challenge-step-${s.id}`}>
                <div className="flex items-start gap-4">
                  <button onClick={() => toggle(s.id)} disabled={busy === s.id} data-testid={`toggle-step-${s.id}`} className="shrink-0 mt-0.5">
                    {busy === s.id ? <Loader2 size={22} className="animate-spin text-slate-500" /> : done ? <CheckCircle2 size={22} className="text-emerald-400" /> : <Circle size={22} className="text-slate-600 hover:text-slate-400" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-eco text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${ch.color}1a`, color: ch.color }}>Day {s.day}</span>
                        <h4 className={`font-display font-semibold ${done ? "text-slate-400 line-through" : "text-white"}`}>{s.title}</h4>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-amber-400 shrink-0"><Zap size={12} /> {s.xp}</span>
                    </div>
                    <p className="text-sm text-slate-300 mt-2">{s.task}</p>
                    {s.detail && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{s.detail}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCert && (
        <Certificate
          name={user?.name}
          title={ch.title}
          badgeIcon={ch.badge.icon}
          badgeTitle={ch.badge.title}
          xp={ch.xp_total}
          date={ch.completed_at}
          onClose={() => setShowCert(false)}
        />
      )}
    </div>
  );
}
